<template>
    <div class="live-training-page">
        <div class="background-overlay" :style="{ backgroundImage: `url(${bgImage})` }"></div>
        <NavigationBar />

        <header class="page-header py-5 text-center">
            <div class="container" data-aos="fade-down">
                <h1 class="display-3 fw-900 text-white">
                    ENTRENAMIENTO <span class="text-danger">PRESENCIAL</span>
                </h1>
                <p class="lead text-secondary text-uppercase letter-spacing-5">
                    Instrucción práctica para el protector moderno
                </p>
            </div>
        </header>

        <div class="container pb-5">
            <div class="search-filter-wrap mb-5" data-aos="fade-up">
                <div class="d-flex flex-column flex-md-row gap-3">
                    <div class="search-box flex-grow-1">
                        <i class="bi bi-search"></i>
                        <input v-model="searchQuery" type="text" placeholder="Nombre o ubicación del curso..."
                            class="form-control-tactical w-100" />
                    </div>
                    <div class="dropdown-container">
                        <select v-model="selectedCategory" class="form-select-tactical w-100">
                            <option value="">Todas las disciplinas</option>
                            <option v-for="category in categories" :key="category" :value="category">{{ category }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="row g-4" v-if="filteredCourses.length > 0">
                <div v-for="course in filteredCourses" :key="course.id" class="col-12 col-lg-6" data-aos="zoom-in">
                    <div class="course-op-card">
                        <div class="op-img-container">
                            <div class="op-bg" :style="{ backgroundImage: `url(${course.image})` }"></div>
                            <div class="op-status-badge">{{ course.category }}</div>
                        </div>
                        <div class="op-info p-4">
                            <div class="d-flex justify-content-between align-items-start mb-2">
                                <h3 class="op-title">{{ course.name }}</h3>
                                <span class="op-price">${{ course.price }}</span>
                            </div>
                            <p class="text-secondary small mb-3"><i class="bi bi-geo-alt-fill text-danger"></i> {{
                                course.fullLocation }}</p>
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="op-date fw-bold"><i class="bi bi-calendar-check me-2"></i> {{ course.date }}
                                </div>
                                <button @click="goToCourse(course.id)" class="btn-tactical-sm">Ver detalles</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="text-center py-5" data-aos="fade-in">
                <i class="bi bi-shield-exclamation display-1 text-secondary opacity-25"></i>
                <h3 class="text-white mt-3">
                    No se encontraron cursos para esta búsqueda.
                </h3>
                <button @click="resetFilters" class="btn btn-link text-danger fw-bold">
                    Reiniciar búsqueda
                </button>
            </div>
        </div>

        <Footer />
    </div>
</template>

<script setup>
import NavigationBar from "@/components/Layout/NavigationBar.vue";
import Footer from "@/components/Layout/Footer.vue";
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getLiveTrainingList } from '@/utils/api_backend';
import bgImage from "@/assets/img/Tactical/tactical_10.jpg";

const searchQuery = ref("");
const selectedCategory = ref("");
const router = useRouter();
const courses = ref([]);
const loading = ref(true);

// Función para navegar a la página de detalles del curso
const goToCourse = (id) => {
    router.push({ name: 'CourseDetail', params: { id: id } });
};

const fetchCourses = async () => {
    try {
        loading.value = true;
        // Petición a: /api/v1/courses/live-training/
        const data = await getLiveTrainingList();
        courses.value = data;
    } catch (error) {
        console.error("Intel failure: Could not retrieve training list", error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchCourses();
});

// LÓGICA DE FILTRADO REACTIVO
const filteredCourses = computed(() => {
    return courses.value.filter(course => {
        const matchesSearch = course.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            course.fullLocation.toLowerCase().includes(searchQuery.value.toLowerCase());
        const matchesCategory = selectedCategory.value === "" || course.category.toUpperCase() === selectedCategory.value.toUpperCase();
        return matchesSearch && matchesCategory;
    });
});

const resetFilters = () => {
    searchQuery.value = "";
    selectedCategory.value = "";
};

const categories = computed(() => {
    // Obtenemos todos los campos 'category' de los cursos
    const allCategories = courses.value.map(course => course.category.toUpperCase());

    // Usamos Set para quitar duplicados y volvemos a convertir a Array
    return [...new Set(allCategories)].sort();
});
</script>

<style scoped>
.live-training-page {
    /* background-color: #000; */
    min-height: 100vh;
    color: white;
    /* AGREGA ESTO: Empuja el contenido hacia abajo para que no lo tape la barra */
    /* Ajusta los 100px según la altura real de tu NavigationBar */
    padding-top: 80px;
}

.fw-900 {
    font-weight: 900;
}

.letter-spacing-5 {
    letter-spacing: 5px;
}

/* BARRA DE BÚSQUEDA TÁCTICA */
.search-box {
    position: relative;
    width: 100%;
}

.search-box i {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #ff3131;
}

.form-control-tactical,
.form-select-tactical {
    background: #111;
    border: 1px solid #333;
    color: white;
    padding: 12px 12px 12px 45px;
    border-radius: 0;
    transition: all 0.3s;
}

.form-select-tactical {
    padding-left: 15px;
}

.form-control-tactical:focus,
.form-select-tactical:focus {
    background: #1a1a1a;
    border-color: #ff3131;
    box-shadow: 0 0 10px rgba(255, 49, 49, 0.2);
    outline: none;
}

/* COURSE CARDS ESTILO PANORÁMICO */
.course-op-card {
    background: #0a0a0a;
    border: 1px solid #222;
    overflow: hidden;
    transition: all 0.4s ease;
}

.course-op-card:hover {
    transform: translateY(-5px);
    border-color: #ff3131;
}

.op-img-container {
    position: relative;
    height: 300px;
    overflow: hidden;
}

.op-bg {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    filter: grayscale(40%) brightness(0.8);
    transition: 0.5s;
}

.course-op-card:hover .op-bg {
    filter: grayscale(0%) brightness(1);
    transform: scale(1.05);
}

.op-status-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    background: #ff3131;
    color: white;
    padding: 5px 12px;
    font-weight: 900;
    font-size: 0.7rem;
    letter-spacing: 2px;
}

.op-title {
    font-weight: 900;
    font-size: 1.5rem;
    text-transform: uppercase;
    color: white;
}

.op-price {
    font-weight: 900;
    font-size: 1.4rem;
    color: #ff3131;
}

.btn-tactical-sm {
    background: transparent;
    border: 1px solid #fff;
    color: #fff;
    padding: 8px 20px;
    font-weight: 800;
    font-size: 0.75rem;
    transition: 0.3s;
}

.btn-tactical-sm:hover {
    background: #fff;
    color: #000;
}

/* Ajustes específicos para escritorio (Tablet en adelante) */
@media (min-width: 768px) {
    .search-box {
        width: 60%;
        /* El buscador toma más espacio en escritorio */
    }

    .dropdown-container {
        width: 250px;
        /* Ancho fijo elegante para el dropdown en PC */
        margin-left: auto;
        /* Lo empuja a la derecha solo en PC */
    }
}

/* Espacio más reducido solo para móvil */
@media (max-width: 1199px) {
    .live-training-page {
        padding-top: 50px; 
    }
}

.background-overlay {
    position: fixed;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: brightness(0.25) blur(4px);
    z-index: -1;
    pointer-events: none;
}
</style>